import { FolderOpen, Upload } from 'lucide-react';
import { useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { useEvalCaseUploads } from './useEvalCaseUploads';

// A finished row shows its result instead: Uploaded or Already uploaded
const STATUS_LABELS = {
  pending: 'Waiting',
  uploading: 'Uploading',
  done: 'Uploaded',
  error: 'Failed',
} as const;

export default function EvalCasesPage() {
  const filesInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const { entries, busy, selectFiles, upload } = useEvalCaseUploads();
  const [copyStatus, setCopyStatus] = useState('');
  const completed = entries.flatMap((entry) =>
    entry.result ? [entry.result] : []
  );
  const failed = entries.filter((entry) => entry.status === 'error').length;

  async function copyIds() {
    try {
      await navigator.clipboard.writeText(
        completed.map((result) => result.upload_id).join('\n')
      );
      setCopyStatus('Copied upload IDs.');
    } catch {
      setCopyStatus(
        'Could not copy. Select the IDs below and copy them manually.'
      );
    }
  }

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6">
      <div>
        <h2 className="text-xl font-semibold">Eval cases</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Upload case files for any eval using your BTS login.
        </p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Upload cases</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Select JSON files with source_votes and base64 content, or a folder
            of them. Files whose names do not end in .json, or start with a dot,
            are skipped. Each case can contain up to 10 MiB. Include every
            source conversation, even if you edited or redacted its contents.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => filesInput.current?.click()}
            >
              <Upload className="mr-2 h-4 w-4" />
              Select files
            </Button>
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => folderInput.current?.click()}
            >
              <FolderOpen className="mr-2 h-4 w-4" />
              Select folder
            </Button>
            <input
              ref={filesInput}
              type="file"
              accept=".json"
              multiple
              hidden
              aria-label="Case files"
              onChange={(event) => {
                selectFiles(event.target.files);
                setCopyStatus('');
                event.target.value = '';
              }}
            />
            <input
              ref={(node) => {
                folderInput.current = node;
                node?.setAttribute('webkitdirectory', '');
              }}
              type="file"
              multiple
              hidden
              aria-label="Case folder"
              onChange={(event) => {
                selectFiles(event.target.files);
                setCopyStatus('');
                event.target.value = '';
              }}
            />
          </div>
          <p className="text-sm text-muted-foreground">
            For each source conversation, an upload file names the vote whose
            sharing permission it relies on; include only messages from before
            that vote gave its sharing permission. An upload stays covered while
            each declared vote is on its declared conversation, was given by
            that conversation's owner, and still holds its sharing permission.
            Each declared conversation must also still exist, with an owner who
            has not signed a BAA. Once the upload is no longer covered, BTS
            refuses the file, and weekly cleanup revokes (deletes the contents
            of) the upload; a revoked file is refused for good. Uploading a
            covered file again returns the same upload ID.
          </p>
          <details className="text-sm">
            <summary className="cursor-pointer font-medium">
              Prepare an upload file
            </summary>
            <p className="my-2 text-muted-foreground">
              Run from backend, replacing the file paths, the conversation UUID
              and the ID of the vote whose shared history the file holds:
            </p>
            <pre className="overflow-x-auto rounded-md bg-muted p-3 text-xs">
              uv run python -m lb.features.eval.replay.workbench.prepare
              case.json --conversation CHAT_UUID:VOTE_ID --output upload.json
            </pre>
            <p className="mt-2 text-muted-foreground">
              Repeat --conversation for multiple sources.
            </p>
          </details>
        </CardContent>
      </Card>
      {entries.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Selected files</CardTitle>
            <p className="text-sm text-muted-foreground" role="status">
              {entries.length} selected, {completed.length} uploaded, {failed}{' '}
              failed
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <Button
                disabled={busy || completed.length === entries.length}
                onClick={() => void upload()}
              >
                {busy
                  ? 'Uploading...'
                  : failed
                    ? 'Retry remaining files'
                    : 'Upload'}
              </Button>
              <Button
                variant="outline"
                disabled={!completed.length}
                onClick={() => void copyIds()}
              >
                Copy upload IDs
              </Button>
            </div>
            {copyStatus && (
              <p role="status" className="text-sm">
                {copyStatus}
              </p>
            )}
            <ul className="divide-y rounded-md border">
              {entries.map((entry, index) => (
                <li key={index} className="space-y-1 p-4">
                  <div className="flex flex-wrap justify-between gap-2 text-sm">
                    <span className="break-all font-medium">
                      {entry.file.webkitRelativePath || entry.file.name}
                    </span>
                    <span className="text-muted-foreground">
                      {entry.result
                        ? entry.result.status === 'created'
                          ? 'Uploaded'
                          : 'Already uploaded'
                        : STATUS_LABELS[entry.status]}
                    </span>
                  </div>
                  {entry.result && (
                    <code className="block break-all text-xs">
                      {entry.result.upload_id}
                    </code>
                  )}
                  {entry.error && (
                    <p className="text-sm text-destructive" role="alert">
                      {entry.error}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
